import { useApp } from '../context/AppContext';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine,
} from 'recharts';
import { FlaskConical, TrendingUp, TrendingDown, Target, AlertTriangle } from 'lucide-react';

export default function SimulatorModule() {
  const { simResult, settings } = useApp();
  const { trades, equityCurve, metrics } = simResult;
  const capital = settings.simCapital || 500000;

  return (
    <div className="p-6 max-w-[1600px] mx-auto" data-testid="simulator-module">
      <h1 className="font-heading text-2xl font-semibold tracking-tight flex items-center gap-2 mb-1">
        <FlaskConical className="w-6 h-6 text-trading-blue" />
        Simulator
      </h1>
      <p className="text-sm text-trading-muted mb-6">Auto-generated paper trades based on signal confirmations</p>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <MetricCard label="Total Trades" value={metrics.totalTrades} testId="sim-total-trades" />
        <MetricCard label="Win Rate" value={`${metrics.winRate}%`} color={metrics.winRate >= 50 ? 'text-trading-green' : 'text-trading-red'} testId="sim-win-rate" />
        <MetricCard label="Expectancy" value={`PHP ${metrics.expectancy.toLocaleString()}`} color={metrics.expectancy >= 0 ? 'text-trading-green' : 'text-trading-red'} testId="sim-expectancy" />
        <MetricCard label="Avg Win" value={`PHP ${metrics.avgWin.toLocaleString()}`} color="text-trading-green" testId="sim-avg-win" />
        <MetricCard label="Avg Loss" value={`PHP ${metrics.avgLoss.toLocaleString()}`} color="text-trading-red" testId="sim-avg-loss" />
        <MetricCard label="Max Drawdown" value={`${metrics.maxDrawdown}%`} color="text-trading-amber" testId="sim-max-dd" />
      </div>

      {/* Equity Curve */}
      <div className="bg-trading-surface border border-trading-border rounded-sm p-4 mb-6" data-testid="sim-equity-curve">
        <h3 className="text-xs uppercase tracking-widest text-trading-muted font-semibold mb-3">Equity Curve</h3>
        {equityCurve.length > 1 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={equityCurve}>
              <CartesianGrid stroke="#242C3D" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fill: '#94A3B8', fontSize: 10 }} interval="preserveStartEnd" />
              <YAxis tick={{ fill: '#94A3B8', fontSize: 10 }} domain={['auto', 'auto']} />
              <Tooltip
                contentStyle={{ backgroundColor: '#10141D', border: '1px solid #242C3D', fontSize: 12 }}
                formatter={(val) => [`PHP ${Number(val).toLocaleString()}`, 'Equity']}
              />
              <ReferenceLine y={capital} stroke="#242C3D" strokeDasharray="5 5" label={{ value: 'Initial', fill: '#94A3B8', fontSize: 10 }} />
              <Line type="monotone" dataKey="equity" stroke="#3B82F6" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[300px] flex items-center justify-center text-trading-muted text-sm">
            No trades generated yet. Upload data and check for CONFIRMED signals.
          </div>
        )}
      </div>

      {/* Trades Table */}
      <div className="bg-trading-surface border border-trading-border rounded-sm overflow-hidden" data-testid="sim-trades-table">
        <div className="px-4 py-2 border-b border-trading-border bg-trading-elevated">
          <span className="text-xs uppercase tracking-widest text-trading-muted font-semibold">
            Simulated Trades ({trades.length})
          </span>
        </div>
        {trades.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead>
                <tr className="bg-trading-elevated/50 border-b border-trading-border">
                  <th>Symbol</th>
                  <th>Entry Date</th>
                  <th>Entry</th>
                  <th>Exit Date</th>
                  <th>Exit</th>
                  <th>Shares</th>
                  <th>P&L</th>
                  <th>Exit Reason</th>
                </tr>
              </thead>
              <tbody>
                {trades.map((t, i) => (
                  <tr key={i} className="border-b border-trading-border/50 hover:bg-trading-elevated/30">
                    <td className="font-semibold text-trading-text">{t.symbol}</td>
                    <td className="text-trading-muted">{t.entryDate}</td>
                    <td>{t.entryPrice?.toFixed(2)}</td>
                    <td className="text-trading-muted">{t.exitDate}</td>
                    <td>{t.exitPrice?.toFixed(2)}</td>
                    <td>{t.shares?.toLocaleString()}</td>
                    <td className={t.pnl >= 0 ? 'text-trading-green' : 'text-trading-red'}>
                      {t.pnl >= 0 ? '+' : ''}{t.pnl?.toLocaleString()}
                    </td>
                    <td>
                      <ExitReasonBadge reason={t.exitReason} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center text-trading-muted text-sm">
            No simulated trades yet.
          </div>
        )}
      </div>
    </div>
  );
}

function MetricCard({ label, value, color, testId }) {
  return (
    <div data-testid={testId} className="bg-trading-surface border border-trading-border rounded-sm p-3">
      <span className="text-[10px] uppercase tracking-widest text-trading-muted block mb-1">{label}</span>
      <span className={`font-mono text-lg tabular-nums ${color || 'text-trading-text'}`}>{value}</span>
    </div>
  );
}

function ExitReasonBadge({ reason }) {
  const colors = {
    STOP_LOSS: 'bg-trading-red/10 text-trading-red border-trading-red/20',
    DISTRIBUTION: 'bg-trading-amber/10 text-trading-amber border-trading-amber/20',
    TIME_STOP: 'bg-trading-muted/10 text-trading-muted border-trading-muted/20',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-sm text-[10px] font-mono border ${colors[reason] || ''}`}>
      {reason?.replace('_', ' ')}
    </span>
  );
}
